// ============================================================
// Dashboard Page — 儀表板總覽
// 今日排班時間軸 + 出勤狀態 + 待簽核 + 本週 KPI
// ============================================================

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { formatTaipeiTime } from '../lib/datetime';

interface TimelineEntry {
  scheduleId: string;
  userId: string;
  userName: string;
  userAvatar?: string;
  startTime: string;
  endTime: string;
  position?: string;
  templateName?: string;
  templateColor?: string;
  clockIn: string | null;
  clockOut: string | null;
  status: 'NORMAL' | 'LATE' | 'EARLY_LEAVE' | 'ABSENT' | 'NOT_YET' | 'UPCOMING' | 'WORKING' | 'PENDING';
}

interface PendingLeave {
  id: string;
  leaveType: string;
  startDate: string;
  endDate: string;
  hours: number;
  reason?: string;
  user: { id: string; name: string; avatar?: string };
}

interface UnfinishedAttendance {
  id: string;
  userId: string;
  userName: string;
  date: string | null;
  clockIn: string | null;
}

interface DashboardData {
  /** 有上班卡但沒有下班卡、且已非今日的紀錄 */
  unfinishedAttendances: UnfinishedAttendance[];
  today: {
    date: string;
    scheduledCount: number;
    clockedInCount: number;
    workingCount: number;
    onTimeCount: number;
    lateCount: number;
    absentCount: number;
    earlyLeaveCount: number;
    timeline: TimelineEntry[];
    /** 今日排定休假者（不列入應到／缺勤） */
    restToday?: { userId: string; userName: string; kind: string; templateName: string }[];
  };
  pending: {
    attendanceCount: number;
    leaveCount: number;
    total: number;
    recentLeaves: PendingLeave[];
  };
  weekKpi: {
    weekStart: string;
    weekEnd: string;
    scheduleCount: number;
    totalWorkHours: number;
    normalCount: number;
    lateCount: number;
    totalRevenue: number;
    activeStaffCount: number;
    attendanceRate: number;
  };
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  NORMAL: { label: '正常', color: 'text-sage-600', bg: 'bg-sage-100' },
  LATE: { label: '遲到', color: 'text-rose-500', bg: 'bg-rose-100' },
  EARLY_LEAVE: { label: '早退', color: 'text-amber-600', bg: 'bg-amber-100' },
  ABSENT: { label: '缺勤', color: 'text-red-600', bg: 'bg-red-100' },
  NOT_YET: { label: '未到', color: 'text-morandi-400', bg: 'bg-morandi-100' },
  // 後端會回 WORKING / UPCOMING，之前這裡沒有對應，會落到預設值 NOT_YET —
  // 人明明已經打卡在店裡工作，儀表板卻寫「未到」。
  WORKING: { label: '工作中', color: 'text-sage-600', bg: 'bg-sage-100' },
  UPCOMING: { label: '未開始', color: 'text-morandi-400', bg: 'bg-morandi-100' },
  PENDING: { label: '待審', color: 'text-blue-600', bg: 'bg-blue-100' },
};

const LEAVE_TYPE_LABEL: Record<string, string> = {
  ANNUAL: '特休', SICK: '病假', PERSONAL: '事假', OFFICIAL: '公假',
  MENSTRUAL: '生理假', FAMILY_CARE: '家庭照顧假', MATERNITY: '產假',
  PATERNITY: '陪產假', UNPAID: '無薪假', BEREAVEMENT: '喪假',
};

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

export function DashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get<DashboardData>('/dashboard');
      setData(res);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    // 每 60 秒自動刷新
    const interval = setInterval(fetchDashboard, 60000);
    return () => clearInterval(interval);
  }, [fetchDashboard]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-morandi-400">載入中...</div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-rose-400">{error}</div>
      </div>
    );
  }

  if (!data) return null;

  const { today, pending, weekKpi } = data;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-5">
      {/* Header */}
      {/* 手機：標題與快捷鍵上下排，快捷鍵可左右滑動（原本六顆擠在同一行會被壓成直書）
          平板／電腦：同一行右對齊 */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-xl font-bold text-morandi-800">儀表板</h1>
          <p className="text-sm text-morandi-400 mt-0.5">
            {new Date().toLocaleDateString('zh-TW', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })}
          </p>
        </div>
        <div className="flex items-center gap-1.5 overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 md:flex-wrap md:justify-end pb-1 md:pb-0">
          {[
            { label: '出勤月報', to: '/attendance-report' },
            { label: '門店報表', to: '/revenue-report' },
            { label: '休假總覽', to: '/leave-overview' },
            { label: '薪資結算', to: '/payroll' },
            { label: '行事曆',  to: '/holidays' },
          ].map(b => (
            <button
              key={b.to}
              onClick={() => navigate(b.to)}
              className="shrink-0 text-sm text-morandi-500 hover:text-sage-700 transition-colors px-3 py-1.5 rounded-btn bg-white md:bg-transparent hover:bg-sage-50 border border-morandi-200 md:border-transparent"
            >
              {b.label}
            </button>
          ))}
          <button
            onClick={fetchDashboard}
            className="shrink-0 text-sm text-morandi-500 hover:text-sage-700 transition-colors px-3 py-1.5 rounded-btn bg-white md:bg-transparent hover:bg-sage-50 border border-morandi-200 md:border-transparent"
          >
            重新整理
          </button>
        </div>
      </div>

      {/* ─── 今日出勤摘要卡片 ─── */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="應到" value={today.scheduledCount} unit="人" color="text-morandi-700" />
        <StatCard label="正常" value={today.onTimeCount} unit="人" color="text-sage-600" />
        <StatCard label="遲到" value={today.lateCount} unit="人" color="text-rose-500" highlight={today.lateCount > 0} />
        <StatCard label="缺勤" value={today.absentCount} unit="人" color="text-red-600" highlight={today.absentCount > 0} />
        <StatCard label="工作中" value={today.workingCount} unit="人" color="text-earth-500" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* ─── 左欄：今日排班時間軸 ─── */}
        <div className="lg:col-span-2 card-morandi p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-morandi-700">今日排班</h2>
            <span className="text-xs text-morandi-400">{today.clockedInCount}/{today.scheduledCount} 已到</span>
          </div>

          {today.timeline.length === 0 ? (
            <div className="text-center py-10 text-morandi-300 text-sm">今日沒有排班</div>
          ) : (
            <div className="space-y-2 max-h-[420px] overflow-y-auto scrollbar-hide">
              {today.timeline.map((entry) => {
                const cfg = STATUS_CONFIG[entry.status] ?? STATUS_CONFIG.NOT_YET;
                return (
                  <div
                    key={entry.scheduleId}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-morandi-50 hover:bg-morandi-100 transition-colors"
                  >
                    {/* 頭像 */}
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium shrink-0"
                      style={{
                        backgroundColor: entry.templateColor ? `${entry.templateColor}30` : '#e8e4df',
                        color: entry.templateColor ?? '#8a7e72',
                      }}
                    >
                      {entry.userName.slice(0, 1)}
                    </div>

                    {/* 名字 + 班別 */}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-morandi-700 truncate">
                        {entry.userName}
                        {entry.position && (
                          <span className="ml-1.5 text-xs text-morandi-400">{entry.position}</span>
                        )}
                      </div>
                      <div className="text-xs text-morandi-400 font-mono">
                        {entry.startTime}–{entry.endTime}
                        {entry.templateName && (
                          <span className="ml-1.5 text-morandi-300">({entry.templateName})</span>
                        )}
                      </div>
                    </div>

                    {/* 打卡時間 */}
                    <div className="text-right shrink-0">
                      <div className="text-xs font-mono text-morandi-500">
                        {entry.clockIn ? formatTaipeiTime(entry.clockIn) : '--:--'}
                        {' → '}
                        {entry.clockOut ? formatTaipeiTime(entry.clockOut) : '--:--'}
                      </div>
                    </div>

                    {/* 狀態 */}
                    <span className={`badge ${cfg.bg} ${cfg.color} shrink-0`}>
                      {cfg.label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ─── 右欄 ─── */}
        <div className="space-y-5">
          {/* 今日休假：讓老闆看得到「誰今天排休」，而不是消失不見 */}
          {(data.today.restToday?.length ?? 0) > 0 && (
            <div className="card-morandi p-4">
              <h2 className="font-semibold text-morandi-700 mb-2">今日休假</h2>
              <div className="space-y-1.5">
                {data.today.restToday!.map(r => (
                  <div key={r.userId} className="flex items-center justify-between text-sm">
                    <span className="text-morandi-600">{r.userName}</span>
                    <span className="badge bg-morandi-100 text-morandi-500">{r.templateName}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 未完成打卡：忘記打下班卡的人，該日工時會算 0 */}
          {data.unfinishedAttendances?.length > 0 && (
            <div className="card-morandi p-4 border border-amber-300 bg-amber-50">
              <div className="flex items-center justify-between mb-2">
                <h2 className="font-semibold text-amber-800">未完成打卡</h2>
                <span className="badge bg-amber-200 text-amber-800">
                  {data.unfinishedAttendances.length}
                </span>
              </div>
              <p className="text-xs text-amber-700 mb-3">
                以下紀錄只有上班卡、沒有下班卡，該日工時會算成 0，薪資也會短少。
                請用「出勤月報」補上下班時間。
              </p>
              <div className="space-y-1.5">
                {data.unfinishedAttendances.map(u => (
                  <div key={u.id} className="flex items-center justify-between text-sm">
                    <span className="text-amber-900">{u.userName}</span>
                    <span className="font-mono text-xs text-amber-700">
                      {u.date} 上班 {formatTaipeiTime(u.clockIn)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 待簽核 */}
          <div className="card-morandi p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-morandi-700">待簽核</h2>
              {pending.total > 0 && (
                <span className="badge bg-rose-100 text-rose-500">{pending.total}</span>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <button
                onClick={() => navigate('/approvals')}
                className="flex flex-col items-center p-3 rounded-lg bg-morandi-50 hover:bg-morandi-100 transition-colors"
              >
                <span className="text-lg font-bold text-morandi-700">{pending.attendanceCount}</span>
                <span className="text-xs text-morandi-400">出勤異常</span>
              </button>
              <button
                onClick={() => navigate('/approvals')}
                className="flex flex-col items-center p-3 rounded-lg bg-morandi-50 hover:bg-morandi-100 transition-colors"
              >
                <span className="text-lg font-bold text-morandi-700">{pending.leaveCount}</span>
                <span className="text-xs text-morandi-400">假單申請</span>
              </button>
            </div>

            {pending.recentLeaves.length > 0 && (
              <div className="space-y-2">
                <div className="text-xs text-morandi-400 mb-1">最新假單</div>
                {pending.recentLeaves.map((leave) => (
                  <div key={leave.id} className="flex items-center gap-2 text-sm">
                    <div className="w-6 h-6 rounded-full bg-earth-100 flex items-center justify-center text-earth-600 text-xs shrink-0">
                      {leave.user.name.slice(0, 1)}
                    </div>
                    <span className="text-morandi-600 truncate">{leave.user.name}</span>
                    <span className="badge bg-morandi-100 text-morandi-500 text-xs">
                      {LEAVE_TYPE_LABEL[leave.leaveType] ?? leave.leaveType}
                    </span>
                    <span className="text-xs text-morandi-300 ml-auto shrink-0">
                      {formatDate(leave.startDate)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {pending.total === 0 && (
              <div className="text-center py-4 text-morandi-300 text-sm">沒有待簽核項目</div>
            )}
          </div>

          {/* 本週 KPI */}
          <div className="card-morandi p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-morandi-700">本週概況</h2>
              <span className="text-xs text-morandi-400">
                {formatDate(weekKpi.weekStart)}–{formatDate(weekKpi.weekEnd)}
              </span>
            </div>

            <div className="space-y-3">
              <KpiRow label="排班數" value={weekKpi.scheduleCount} unit="班" />
              <KpiRow label="總工時" value={weekKpi.totalWorkHours} unit="小時" />
              <KpiRow
                label="出勤率"
                value={weekKpi.attendanceRate}
                unit="%"
                highlight={weekKpi.attendanceRate < 90}
              />
              <KpiRow label="遲到次數" value={weekKpi.lateCount} unit="次" highlight={weekKpi.lateCount > 0} />
              {weekKpi.totalRevenue > 0 && (
                <KpiRow label="營收" value={weekKpi.totalRevenue.toLocaleString()} unit="元" />
              )}
              <KpiRow label="在職員工" value={weekKpi.activeStaffCount} unit="人" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-Components ────────────────────────────────────────

function StatCard({ label, value, unit, color, highlight }: {
  label: string;
  value: number;
  unit: string;
  color: string;
  highlight?: boolean;
}) {
  return (
    <div className={`card-morandi p-3 text-center ${highlight ? 'ring-1 ring-rose-300' : ''}`}>
      <div className={`text-2xl font-bold font-mono ${color}`}>{value}</div>
      <div className="text-xs text-morandi-400 mt-0.5">{label}<span className="ml-0.5">{unit}</span></div>
    </div>
  );
}

function KpiRow({ label, value, unit, highlight }: {
  label: string;
  value: number | string;
  unit: string;
  highlight?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-morandi-500">{label}</span>
      <span className={`text-sm font-medium font-mono ${highlight ? 'text-rose-500' : 'text-morandi-700'}`}>
        {value} <span className="text-xs text-morandi-400">{unit}</span>
      </span>
    </div>
  );
}
